import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

type SupabaseAuthContextType = {
  session: Session | null;
  user: SupabaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const defaultState: SupabaseAuthContextType = {
  session: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  signInWithPassword: async () => ({ error: null }),
  signInWithGoogle: async () => {},
  signOut: async () => {},
};

const SupabaseAuthContext = createContext<SupabaseAuthContextType>(defaultState);

export function SupabaseAuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/home` },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      isAuthenticated: !!session,
      isLoading,
      signInWithPassword,
      signInWithGoogle,
      signOut,
    }),
    [session, isLoading, signInWithPassword, signInWithGoogle, signOut]
  );

  return <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>;
}

export function useAuth(): SupabaseAuthContextType {
  return useContext(SupabaseAuthContext);
}

export default SupabaseAuthProvider;
